import { Tab } from "../types/Tab";
import { Link } from "react-router-dom";
import {
  capitalizeFirstLetter,
  getTabLink,
  parameterToTabType,
  typeToString,
} from "../extensions/helpers";
import { useSelectedLocation } from "../contexts/SelectedLocationContext";
import { useCatalog } from "../contexts/CatalogContext";
import { CatalogObject } from "square";
import { isDevelopment } from "../extensions/environments";

type TabDropdownProps = {
  tab?: Tab;
  domain?: CatalogObject;
};

const TabDropdown = ({ tab, domain }: TabDropdownProps) => {
  const { domain: selectedDomain, category: selectedCategory } =
    useSelectedLocation();
  const { isTheme, getChildCategories } = useCatalog();

  const theme = isTheme(domain?.id ?? "");
  const categories = !!domain
    ? getChildCategories(domain.id)?.filter(
        (c) => c.categoryData?.onlineVisibility || isDevelopment(), // Square Sandbox returns isVisibility = false for child categories without ability to change it
      )
    : [];

  return (
    // tab-item-wrapper drives the hover — hovering the dropdown (an absolute
    // child) keeps this wrapper hovered, so there is no dead zone.
    <div className="tab-item-wrapper relative">
      {/* The domain's own link is `/shop/<domain>` — no category segment.
          This used to append a hardcoded `/category/Apparel`, which only ever
          resolved because the one shoppable domain happens to BE named
          Apparel (`/shop/apparel/category/apparel` finds the same category
          either way). Any second domain would have pointed at Apparel's
          products under its own URL, and renaming Apparel would have killed
          the header link on every page. */}
      <Link
        to={getTabLink({
          tab,
          domain: domain?.categoryData?.name ?? undefined,
        })}
        key={tab}
        // The active tab is marked by weight and colour only. It used to also
        // carry a `border-b-2 border-saddle` underline, which read as a stray
        // rule under "Home" on every page that isn't a shop page.
        className={`tab-button ${theme ? "text-rust" : ""} ${
          (tab != null && parameterToTabType(selectedDomain ?? "") === tab) ||
          (domain != null &&
            selectedDomain === domain?.categoryData?.name?.toLowerCase())
            ? "text-brown-dark font-semibold"
            : "text-navy hover:text-saddle"
        }`}
      >
        {/* No caret next to a tab that has children — the dropdown reveals
            itself on hover and the arrow only added visual noise. */}
        {capitalizeFirstLetter(
          typeToString(tab) ?? domain?.categoryData?.name ?? undefined,
        )}
      </Link>

      {/* NOT Handling Themes in any sub-list */}
      {categories?.length > 0 && (
        <div className="dropdown-content">
          {categories.map((category, i) => (
            <Link
              to={getTabLink({
                tab,
                domain: domain?.categoryData?.name ?? undefined,
                category: category?.categoryData?.name ?? undefined,
              })}
              key={i}
              className={`dropdown-item ${
                selectedCategory === category?.categoryData?.name?.toLowerCase()
                  ? "dropdown-item--active"
                  : ""
              }`}
            >
              {category?.categoryData?.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default TabDropdown;
